import { useEffect, useMemo, useState } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { useQueryClient } from '@tanstack/react-query'
import { Button } from '@/components/ui/button'
import { Card, CardDescription, CardFooter, CardHeader, CardTitle } from '@/components/ui/card'
import { ScrollArea } from '@/components/ui/scroll-area'
import Spinner from '@/components/ui/spinner'
import FullPageSpinner from '@/components/ui/full-page-sinner'
import { FeedbackDialog } from '@/components/form/feedback-dialog'
import {
  useFetchSignatureByIdOwner,
  useCreateSignature,
  useAddSignature,
  useFetchSignatureById
} from '@/features/signatures/services'
import { SignatureQueryKey } from '../helpers/signature-query-key'
import { ROUTE_BUILDERS } from '@/config/routes'
import { SignatureContactVerificationStep } from './signature-contact-verification-step'
import { TermsOfTestemunha } from '@/components/form/termos-testemunha'
import PhotoCapture from './photo-capture'
import SignaturePad from './SignaturePad'
import { useAcceptWitnessTerms } from '@/features/agreement/services/useAcceptWitnessTerms'
import { useUpdateAgreement } from '@/features/agreement/services/useUpdateAgreement'
import { useFetchAgreementById } from '@/features/agreement/services/useFetchAgreementById'
import {
  restoreUserSessionSnapshot,
  useClearAuthState,
  useCurrentClient,
  useSetAuthState
} from '@/features/auth/state/auth'
import { useFetchClientByCnpjCpf } from '@/features/client/services'

interface CapturedData {
  fotoPessoa: File
  fotoDoc: File
  local: string
  maquina: string
}

type FlowStep = 'verify-contacts' | 'capture-docs' | 'signature' | 'decision' | 'witness-terms'

export default function AssinarClient() {
  const [step, setStep] = useState<FlowStep>('verify-contacts')
  const [, setSignature] = useState<File | null>(null)
  const [isLoading, setIsLoading] = useState(false)
  const [isDialogOpen, setIsDialogOpen] = useState(false)
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null)

  const queryClient = useQueryClient()
  const navigate = useNavigate()
  const { id_client } = useParams<{ id_client: string }>()
  const currentClient = useCurrentClient()
  const clearAuthState = useClearAuthState()
  const setAuthState = useSetAuthState()
  const { data: targetClient, isLoading: isLoadingTargetClient } = useFetchClientByCnpjCpf(id_client)
  const { data: agreementData, isLoading: isLoadingAgreement } = useFetchAgreementById(id_client || '')

  const fetchSignatureHook = id_client?.length === 14 ? useFetchSignatureById : useFetchSignatureByIdOwner
  const {
    data: signatureStatus,
    error,
    isLoading: isFetchingSignature
  } = id_client ? fetchSignatureHook(id_client) : { data: null, error: null, isLoading: false }

  const { mutate: addSignature } = useAddSignature()
  const { mutate: createSignature } = useCreateSignature()
  const { mutateAsync: acceptWitnessTerms } = useAcceptWitnessTerms(id_client || '')
  const { mutateAsync: updateAgreement } = useUpdateAgreement(id_client || '')

  const nextWitnessSlot = useMemo(() => {
    if (signatureStatus?.data?.check_testemunha_1) return 2
    return 1
  }, [signatureStatus?.data?.check_testemunha_1])

  const handleLogoutClient = () => {
    const restoredUserSession = restoreUserSessionSnapshot()

    if (restoredUserSession) {
      setAuthState(restoredUserSession)
      navigate(ROUTE_BUILDERS.minute(id_client || ''), { replace: true })
      return
    }

    clearAuthState()
    navigate(ROUTE_BUILDERS.minute(id_client || ''), { replace: true })
  }

  useEffect(() => {
    if (!signatureStatus?.data) return

    if (signatureStatus.data.isModifica === false && !agreementData?.isAssinado) {
      setStep('decision')
    }
  }, [
    agreementData?.isAssinado,
    agreementData?.isTermoWitness,
    agreementData?.witnessesComplete,
    signatureStatus?.data
  ])

  const handlePhotoCaptureComplete = async (data: CapturedData) => {
    setIsLoading(true)

    try {
      await new Promise<void>((resolve, reject) => {
        createSignature(
          {
            id_client,
            fotoPessoa: data.fotoPessoa,
            fotoDoc: data.fotoDoc,
            local: data.local,
            maquina: data.maquina
          },
          {
            onSuccess: async () => {
              await queryClient.refetchQueries({
                queryKey: SignatureQueryKey(id_client)
              })
              resolve()
            },
            onError: error => {
              console.error('Erro ao criar assinatura:', error)
              setMessage({ type: 'error', text: 'Falha ao cadastrar informações.' })
              setIsDialogOpen(true)
              reject(error)
            }
          }
        )
      })
      setStep('signature')
    } catch (error) {
      console.error('Erro durante o processo:', error)
    } finally {
      setIsLoading(false)
    }
  }

  const handleSignatureUser = async (signatureData: File) => {
    setSignature(signatureData)
    setIsLoading(true)

    try {
      await new Promise<void>((resolve, reject) => {
        if (id_client) {
          addSignature(
            {
              id_client,
              assinatura: signatureData
            },
            {
              onSuccess: async () => {
                await queryClient.invalidateQueries({ queryKey: ['agreement'] })
                resolve()
              },
              onError: error => {
                console.error('Erro ao adicionar assinatura:', error)
                setMessage({
                  type: 'error',
                  text: 'Falha ao adicionar assinatura.'
                })
                setIsDialogOpen(true)
                reject(error)
              }
            }
          )
        } else {
          reject('ID do cliente não fornecido.')
        }
      })

      setMessage({
        type: 'success',
        text: 'Assinatura adicionada com sucesso!'
      })
      setIsDialogOpen(true)
      setStep('decision')
    } catch (error) {
      console.error('Erro durante o processo:', error)
    } finally {
      setIsLoading(false)
    }
  }

  const handleCloseWithoutWitnesses = async () => {
    setIsLoading(true)

    try {
      if (!agreementData?.witnessesComplete && !agreementData?.isTermoWitness) {
        await acceptWitnessTerms()
      }
      await updateAgreement()
      navigate(ROUTE_BUILDERS.minute(id_client || ''), { state: { refetchOnMount: true } })
    } catch (error) {
      console.error('Erro ao fechar contrato sem testemunhas:', error)
      setMessage({ type: 'error', text: 'Falha ao aceitar o termo e fechar o contrato.' })
      setIsDialogOpen(true)
    } finally {
      setIsLoading(false)
    }
  }

  if (isLoadingTargetClient || isLoadingAgreement) {
    return (
      <div className="flex items-center justify-center h-full">
        <Spinner />
      </div>
    )
  }

  if (!currentClient || !id_client || !targetClient || String(targetClient.id_owner ?? '') !== String(currentClient.id)) {
    return (
      <div className="min-h-screen bg-gray-100 flex flex-col items-center justify-center p-4">
        <p className="text-lg font-semibold text-red-600 text-center mb-4">
          Você não tem permissão para acessar a confirmação de contatos deste cliente.
        </p>
        <div className="flex ">
          <Button onClick={handleLogoutClient}>Sair como cliente</Button>
        </div>
      </div>
    )
  }

  if (isFetchingSignature) {
    return (
      <div className="flex items-center justify-center h-full">
        <Spinner />
      </div>
    )
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center p-4">
        <p className="text-lg font-semibold text-red-600 text-center">Erro ao verificar assinatura. Tente novamente.</p>
        <div className="flex ">
          <Button onClick={handleLogoutClient}>Sair como cliente</Button>
        </div>
      </div>
    )
  }

  if (agreementData?.isAssinado) {
    return (
      <div className="min-h-screen bg-gray-100 flex flex-col items-center justify-center p-4">
        <p className="text-lg font-semibold text-slate-700 text-center mb-4">Este contrato já foi finalizado.</p>
        <div className="flex ">

          <Button onClick={handleLogoutClient}>Sair como cliente</Button>
        </div>
      </div>
    )
  }

  return (
    <div className="h-full min-w-screen">
      <div className="absolute top-4 right-4 z-20 flex ">
        <Button onClick={handleLogoutClient}>Sair como cliente</Button>
      </div>

      {step === 'verify-contacts' && id_client && (
        <SignatureContactVerificationStep id_client={id_client} onComplete={() => setStep('capture-docs')} />
      )}

      {step === 'capture-docs' && (
        <ScrollArea>
          <div className="flex flex-col items-center justify-center min-h-screen p-8 w-full max-w-4xl mx-auto relative">
            <PhotoCapture onComplete={handlePhotoCaptureComplete} />
          </div>
        </ScrollArea>
      )}

      {step === 'signature' && (
        <>
          <div className="absolute top-16 right-4 z-20">
            <Button
              className="bg-primary hover:bg-primary-hover text-primary-foreground"
              onClick={() => setStep('capture-docs')}
              variant="outline"
            >
              Voltar etapa
            </Button>
          </div>
          <div className="flex flex-col items-center justify-center ">
            <SignaturePad onComplete={handleSignatureUser} id_client={id_client || 'default_id'} />
          </div>
        </>
      )}

      {step === 'decision' && (
        <div className="min-h-screen flex items-center justify-center p-4">
          <Card className="w-full max-w-2xl">
            <CardHeader>
              <CardTitle>Próximo passo da minuta</CardTitle>
              <CardDescription>
                A assinatura foi concluída. Agora você pode coletar as testemunhas ou fechar o contrato sem
                testemunhas, aceitando o termo correspondente.
              </CardDescription>
            </CardHeader>

            <CardFooter className="flex flex-col gap-2 sm:flex-row">
              {!agreementData?.witnessesComplete && (
                <Button
                  className="w-full sm:w-auto"
                  onClick={() => navigate(ROUTE_BUILDERS.signWitness(id_client || '', nextWitnessSlot))}
                >
                  Assinar testemunha {nextWitnessSlot}
                </Button>
              )}
              {!agreementData?.isTermoWitness && (
                <Button variant="outline" className="w-full sm:w-auto" onClick={() => setStep('witness-terms')}>
                  Fechar sem testemunhas
                </Button>
              )}
              {(agreementData?.isTermoWitness || agreementData?.witnessesComplete) && (
                <Button variant="outline" className="w-full sm:w-auto" onClick={handleCloseWithoutWitnesses}>
                  Fechar contrato
                </Button>
              )}

            </CardFooter>
          </Card>
        </div>
      )}

      {step === 'witness-terms' && (
        <TermsOfTestemunha
          title="Termo de testemunha"
          buttonLabel="Aceitar termo e fechar contrato"
          onAccept={handleCloseWithoutWitnesses}
        />
      )}

      {isLoading && <FullPageSpinner />}
      <FeedbackDialog isOpen={isDialogOpen} onOpenChange={setIsDialogOpen} message={message} />
    </div>
  )
}
